/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { dupeSelector, originalSelector, useCreateDuplicateMutation, type duplicateResolution, duplicateSource, type ChildDuplicate } from 'slices/duplicateChildSlice'
import { useAppSelector } from 'hooks/reduxHooks'
import { Box, Button, Checkbox, Grid, Typography, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material'
import { TextFieldWithLabel } from 'Components/TextField'
import { TextAreaWithLabel } from 'Components/TextArea'
import { Modal } from 'Components/Modal'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ButtonGroup, { type buttonOption } from 'Components/Button/ButtonGroup'
import DataTable from 'Components/Table/DataTable'
import { Column, ColumnType, type RowData } from 'Components/Table/DataTable/DataTable.model'
import { usePagination, type PaginationProps } from 'hooks/use-pagination'
import { FormatDateValue } from 'core/Utilities'
import { ProbabilityChip } from 'Components/DuplicateHandling/ProbabilityChip'
import LaunchIcon from '@mui/icons-material/Launch'
import { useGetDuplicateThresholdsQuery } from 'slices/globalSettingSlice'
import { generatePath, useParams } from 'react-router'
import * as AppRoutes from 'Pages/Routes'
import { sendPost, sendPut } from 'hooks/use-fetch'
import { toast } from 'react-toastify'
import { useAuth } from 'hooks/use-auth'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { AccountType, Role, type postError } from 'core/types'
import { DuplicateBreakdown } from './DuplicateBreakdown'
import { DuplicateFound } from 'Pages/CaregiverPortal/Members/DuplicateFound'
import { DuplicateRequestReview } from 'Pages/CaregiverPortal/Members/DuplicateReviewRequest'

export function DuplicateAction (): JSX.Element {
  const [createDupe] = useCreateDuplicateMutation()
  const [resolutionChoice, setResolutionChoice] = useState<number>(0)
  const [selectedDupe, setSelectedDupe] = useState<number | null>(null)
  const [selectionRequiresRequestAccess, setSelectionRequiresRequestAccess] = useState<boolean | null>(null)
  const [reasonNotADupe, setReasonNotADupe] = useState<string | null>(null)
  const [accessRequestNote, setAccessRequestNote] = useState<string | null>(null)
  const buttonOpts: buttonOption[] = [{ id: 0, name: 'Discard Record' }, { id: 1, name: 'Create New Record' }]
  const { data } = useGetDuplicateThresholdsQuery()
  const [childIDRequestingAccess, setChildIDRequestingAccess] = useState<number | null>(null)
  const nav = useNavigate()
  const params = useParams()
  const auth = useAuth()
  const account = useAccountId()
  const sourceSiblingID: number | undefined = params?.id !== undefined ? Number(params.id) : undefined
  const dupeRecord = useAppSelector(dupeSelector)
  const existingRecord = useAppSelector(originalSelector)
  const isNotISDUser = account?.type !== AccountType.ISD

  const paginationProps: PaginationProps<RowData> = {
    rows: existingRecord
  }

  const selectionColumn = new Column('', 'Select', ColumnType.CUSTOM)
  selectionColumn.sortable = false
  selectionColumn.visibility = resolutionChoice === 0
  selectionColumn.customCellGenerator = row => {
    return <Box>
      <Checkbox checked={selectedDupe === row.childId} onChange={(e) => {
        selectionChecked(e.target.checked, row.childId, row.canAccess)
      }}
      />
    </Box>
  }

  const nameColumn = new Column('lastName', 'Student Name', ColumnType.CUSTOM)
  nameColumn.customCellGenerator = row => {
    return <Box>{row.firstName} {row.lastName}</Box>
  }

  const dobColumn = new Column('dateOfBirth', 'DOB', ColumnType.CUSTOM)
  dobColumn.customCellGenerator = row => {
    return <Box>{FormatDateValue(row.dateOfBirth)}</Box>
  }

  const addressColumn = new Column('address', 'Address', ColumnType.CUSTOM)
  addressColumn.customCellGenerator = row => {
    return <>
      <Box>{row.address}</Box>
      <Box>{row.city}, {row.state} {row.zipCode}</Box>
    </>
  }

  const probabilityColumn = new Column('probability', 'Probability', ColumnType.CUSTOM)
  probabilityColumn.customCellGenerator = row => {
    return <ProbabilityChip dupeData={row as ChildDuplicate} acceptanceThreshold={undefined} />
  }

  const actionColumn = new Column('action', 'Action', ColumnType.CUSTOM)
  actionColumn.sortable = false
  actionColumn.customCellGenerator = row => {
    if (row.canAccess) {
      return <>
        <a href={generatePath(AppRoutes.CARENETWORK_MANAGEMENT_DETAILS, { id: row.childId.toString() })} target='_blank' style={{ marginBottom: '0.5rem' }}>Child Details <LaunchIcon fontSize='small' sx={{ ml: 0.5 }} /></a>
      </>
    // } else if (resolutionChoice === 1) {
    //   return <AccessRequestAction
    //     childNotTiedToISD={false}
    //     currentAccessRequestID={null}
    //     currentAccessRequestStatus={null}
    //     requiresAccessRequest={true}
    //     childId={row.childId}
    //     changeMade={() => { } }
    //   />
    } else {
      return <Box>Access Required</Box>
    }
  }

  const columns = !isNotISDUser ? [selectionColumn, nameColumn, dobColumn, addressColumn, probabilityColumn, actionColumn] : [nameColumn, dobColumn, addressColumn, probabilityColumn, actionColumn]

  const pagination = usePagination(paginationProps)

  const handleClick = (val: number): void => {
    setResolutionChoice(val)
  }

  const selectionChecked = (checked: boolean, id: number, canAccess: boolean): void => {
    if (checked) {
      setSelectedDupe(id)
      setSelectionRequiresRequestAccess(!canAccess)
    } else {
      setSelectedDupe(null)
      setSelectionRequiresRequestAccess(null)
      setChildIDRequestingAccess(null)
    }
  }

  const rowClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): void => {
    selectionChecked(selectedDupe !== row.childId, row.childId, row.canAccess)
  }

  const continueWithNewSibling = async (): Promise<void> => {
    const childID = await createChildRecord()
    if (childID != null && sourceSiblingID !== undefined) {
      await sendPost('/CareNetworkEdit/AddSibling', { childID: sourceSiblingID, siblingId: childID })
      nav(generatePath(AppRoutes.CARENETWORK_MANAGEMENT_DETAILS, { id: sourceSiblingID.toString() }))
    }
  }

  const continueWithNewChild = async (): Promise<void> => {
    const childID = await createChildRecord()
    if (childID != null) {
      const editChild = generatePath(AppRoutes.CARENETWORK_MANAGEMENT_DETAILS, { id: childID.toString() })
      nav(editChild)
    }
  }

  const createChildRecord = async (): Promise<number | null> => {
    if (reasonNotADupe === null || reasonNotADupe === '') {
      toast.error('Explanation of why this is not a duplicate is required.')
      return null
    } else {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const submitValue = { ...dupeRecord, matchedValues: existingRecord[0].matchedValues, probability: existingRecord[0].probability } as ChildDuplicate
      const resolution: duplicateResolution = {
        reasoning: reasonNotADupe,
        duplicateId: submitValue?.childId ?? null,
        duplicate: submitValue,
        caregiverID: 0,
        providerID: isNotISDUser ? (account?.id ?? null) : null,
        matchedChildID: existingRecord[0]?.childId ?? 0,
        matchedChildNickName: submitValue?.nickName ?? null,
        source: submitValue?.source ?? duplicateSource.ChildEntry,
        childID: submitValue?.childId ?? null
      }
      const { data, error } = await createDupe(resolution)
      const errorDisplay = error?.toString() ?? null
      if (errorDisplay != null) {
        toast.error('An error occurred while creating the duplicate record. ' + errorDisplay)
        return null
      } else {
        return Number(data)
      }
    }
  }

  const submitAccessRequest = async (): Promise<void> => {
    const request = {
      childId: childIDRequestingAccess,
      accountId: account?.id ?? null,
      accountType: account?.type,
      notes: accessRequestNote ?? '',
      childNotTiedToISD: false,
      siblingId: sourceSiblingID ?? null
    }
    const result = await sendPut('/ChildDetails/RequestAccess', request)
    const rsp: { newRecordID: number, success: boolean, errors: postError[] } = result.response
    if (rsp.success) {
      toast.success('Access Request Submitted')
      if (auth.user?.securityLevel === Role.SERVICE_PROVIDER_USER) {
        nav(AppRoutes.ENROLLMENT_MANAGEMENT)
      } else {
        nav(AppRoutes.FIND_CHILDREN)
      }
    } else {
      rsp.errors.forEach((e: postError) => {
        toast.error(e.error)
      })
    }
  }

  const onUseExistingRequiringAccess = (): void => {
    if (selectedDupe != null) {
      setChildIDRequestingAccess(selectedDupe)
    }
  }

  const onUseExistingSiblingRequiringAccess = (): void => {
    if (selectedDupe != null) {
      // to be handled in MIECC-2118
      setChildIDRequestingAccess(selectedDupe)
    }
  }

  const discardAndViewChild = async (): Promise<void> => {
    if (selectedDupe != null) {
      nav(generatePath(AppRoutes.CARENETWORK_MANAGEMENT_DETAILS, { id: selectedDupe.toString() }))
    }
  }

  const discardUseAsSibling = async (): Promise<void> => {
    if (selectedDupe != null && sourceSiblingID !== undefined) {
      await sendPost('/CareNetworkEdit/AddSibling', { childID: sourceSiblingID, siblingId: selectedDupe })
      nav(generatePath(AppRoutes.CARENETWORK_MANAGEMENT_DETAILS, { id: sourceSiblingID.toString() }))
    }
  }

  const resolveText = dupeRecord?.serviceID != null ? 'Discard and Create Enrollment for Selected Child' : 'Discard and View Selected Child'
  const dateDisplay = dupeRecord?.dateOfBirth != null && dupeRecord?.dateOfBirth !== '' ? dupeRecord.dateOfBirth : dupeRecord?.dueDate
  const dateText = dupeRecord?.dateOfBirth != null && dupeRecord?.dateOfBirth !== '' ? 'DOB' : 'Due Date'
  return <>

    {!isNotISDUser && (<>
      <Typography fontWeight={'bold'} fontSize={'32px'}>{sourceSiblingID === undefined ? 'Review Duplicate' : 'Review Sibling Duplicate'}</Typography>
      <Typography variant="h6">This child may be a duplicate, how would you like to proceed?</Typography>
      <ButtonGroup value={resolutionChoice} options={buttonOpts} setValue={handleClick} />
    </>)}
    {isNotISDUser && (<DuplicateFound isdId={dupeRecord?.isdId ?? 0} type='provider'/>)}

    <Typography variant="h6">Your New Record</Typography>

    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Student Name</TableCell>
          <TableCell>{dateText}</TableCell>
          <TableCell>Address </TableCell>
          <TableCell>Additional Info </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow data-testid="newRecordContainer">
          <TableCell> {dupeRecord?.firstName} {dupeRecord?.lastName} </TableCell>
          <TableCell> {dateDisplay != null ? FormatDateValue(new Date(dateDisplay ?? '')) : ''} </TableCell>
          <TableCell>
            <Box>{dupeRecord?.address} </Box>
            <Box>{dupeRecord?.city}, {dupeRecord?.state} {dupeRecord?.zipCode} </Box>
          </TableCell>
          <TableCell>
            <Box>{dupeRecord?.birthGender}</Box>
            <Box>{dupeRecord?.race}</Box>
            <Box>Speaks {dupeRecord?.requestingUserPrimaryLanguage ?? 'English'}</Box>
          </TableCell>
          <TableCell>
            {dupeRecord?.notes}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>

    <Typography variant="h6">Possible Matches</Typography>

    <DataTable
      page={pagination.page}
      name='selectionTable'
      columns={columns}
      hasCheckbox={false}
      rows={pagination.internalRows}
      onRowClick={isNotISDUser ? undefined : rowClick}
      totalRecords={pagination.recordCount}
      loading={false}
      onSortChange={(col, sorts) => {
        pagination.onSortChange(col, sorts[0].order)
      }}
      onPageChange={pagination.handleChangePage}
      onRowsPerPageChange={pagination.handleChangeRowsPerPage}
      data-testid='existingRecordContainer'
      expandedRowContent={(row) => {
        if (row.childId !== selectedDupe) return null
        return <DuplicateBreakdown dupeData={row as ChildDuplicate} warningTreshold={data?.warningThreshold ?? 50} acceptanceTreshold={101} />
      }}
    />
    {!isNotISDUser && (<>
      {selectedDupe != null && childIDRequestingAccess != null && <Modal
        title='Request Access'
        confirmationContent={<Box>
          <TextAreaWithLabel
            label={'Please give a brief explanation of why you are requesting access to this child'}
            name='accessRequestNote'
            dataTestId='accessRequestNote'
            onChange={(e) => {
              setAccessRequestNote(e.target.value)
            }}
            value={accessRequestNote}
          />

        </Box>}
        confirmButtonText='Submit'
        cancelButtonText='Cancel'
        onClose={() => {
          setChildIDRequestingAccess(null)
        }}
        onConfirm={submitAccessRequest}
        open={true}
      />
    }
      {resolutionChoice === 1 && <TextFieldWithLabel
        label='Explain why you think this child is not actually a duplicate'
        name='reasonNotADupe'
        value={reasonNotADupe}
        onChange={(e) => { setReasonNotADupe(e.target.value) }}
      />
    }
      <Grid container flexDirection='row' sx={{ justifyContent: 'space-between' }} >
        <Grid item>
          <Button color='secondary' variant='outlined' onClick={() => { nav(-1) }} >Go Back to Child Details</Button>
        </Grid>
        <Grid item>
          {resolutionChoice === 1 && sourceSiblingID === undefined && <Button color='primary' variant='contained' onClick={continueWithNewChild}>Continue with New Child</Button>}
          {resolutionChoice === 1 && sourceSiblingID !== undefined && <Button color='primary' variant='contained' onClick={continueWithNewSibling}>Continue with New Sibling</Button>}
          {resolutionChoice === 0 && <>
            {selectedDupe == null && <Button color='primary' variant='contained' disabled>Pending Selection</Button>}
            {selectedDupe != null && selectionRequiresRequestAccess && sourceSiblingID === undefined && <Button color='primary' variant='contained' onClick={onUseExistingRequiringAccess}>Discard and Request Access</Button>}
            {selectedDupe != null && selectionRequiresRequestAccess && sourceSiblingID !== undefined && <Button color='primary' variant='contained' onClick={onUseExistingSiblingRequiringAccess}>Discard and Request Access</Button>}
            {selectedDupe != null && selectionRequiresRequestAccess === false && sourceSiblingID === undefined && <Button color='primary' variant='contained' onClick={discardAndViewChild} data-testid='discardAndViewSelectedChild'>{resolveText}</Button>}
            {selectedDupe != null && selectionRequiresRequestAccess === false && sourceSiblingID !== undefined && <Button color='primary' variant='contained' onClick={discardUseAsSibling} data-testid='discardAndAddSelectedSibling'>Discard and Add Selected Sibling</Button>}
          </>}
        </Grid>
      </Grid>
    </>)}
    {isNotISDUser && dupeRecord != null && (<>
      <DuplicateRequestReview isIntakeForm={false} userId={account?.id ?? null} userType='provider' />
    </>)
    }
  </>
}
